import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Notification } from '../../types/notifications';
import { formatNotificationDate } from '../../types/notifications';
import { useNavigate } from 'react-router-dom';
import { markAsRead, deleteNotification } from '../../hooks/useNotifications';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: number) => void;
  onDelete: (id: number) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
}) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const isRead = !!notification.readAt;

  const handleClick = async () => {
    if (!isRead) {
      try {
        await markAsRead(notification.id);
        onMarkAsRead(notification.id);
      } catch (error: any) {
        showError(error?.message || 'Failed to mark notification as read');
      }
    }

    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteNotification(notification.id);
      onDelete(notification.id);
    } catch (error: any) {
      showError(error?.message || 'Failed to delete notification');
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'p-4 border-b cursor-pointer transition-colors',
        colors.border,
        colors.interactiveHover,
        !isRead && cn(colors.surfaceHover, colors.primaryBorder),
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn('flex-shrink-0 mt-0.5', colors.primaryFg)}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm leading-snug', !isRead ? 'font-semibold' : 'font-normal', colors.text)}>
            {notification.message}
          </p>
          <p className={cn('text-xs mt-1.5', colors.textMuted)}>
            {formatNotificationDate(notification.createdAt)}
          </p>
        </div>
        {!isRead && (
          <div className="flex-shrink-0 pt-1.5">
            <div className={cn('w-2 h-2 rounded-full', colors.primary)} />
          </div>
        )}
        <button
          onClick={handleDelete}
          className={cn('flex-shrink-0 transition-colors', colors.textMuted, 'hover:text-red-600')}
          title="Delete notification"
          type="button"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default NotificationItem;
